import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CLIMATIZACAO_LABELS, PAPEL_LABELS, WINE_TYPE_LABELS } from '@adega/shared';
import { BottleCard } from '../components/BottleCard.js';
import { CellarGrid } from '../components/CellarGrid.js';
import { EscolhaClimatizacao } from '../components/EscolhaClimatizacao.js';
import { EscolhaPapel } from '../components/EscolhaPapel.js';
import { EmptyState, ErrorBox, Loading } from '../components/Feedback.js';
import { PageHeader } from '../components/PageHeader.js';
import { ShelfEditor } from '../components/ShelfEditor.js';
import { useBottles, useCellar, useUpdateCellar } from '../api/queries.js';

export function CellarView() {
  const { cellarId } = useParams();
  const navigate = useNavigate();

  const idSelecionada = cellarId ? Number(cellarId) : undefined;
  const { data: cellar, isLoading, error } = useCellar(idSelecionada);
  // Garrafas sem posição são por adega: cada móvel tem a sua fila de espera.
  const { data: semPosicao } = useBottles({ unplaced: true, cellarId: cellar?.id });
  const [editando, setEditando] = useState(false);
  const atualizar = useUpdateCellar();

  if (isLoading) return <Loading texto="Carregando sua adega…" />;
  // Só troca a tela pelo erro quando não há nada para mostrar. Com a adega já
  // carregada, uma reconsulta que falha é assunto da faixa de conexão — apagar o
  // grid por uma oscilação tira da pessoa justamente o que ela veio ver.
  if (error && !cellar) return <ErrorBox erro={error} />;
  if (!cellar) {
    return (
      <div>
        <PageHeader titulo="Adega" voltar="/" />
        <EmptyState
          titulo="Adega não encontrada"
          descricao="Ela pode ter sido removida, ou pertence a outra conta."
          acao={
            <Link to="/" className="btn-primary">
              Minhas adegas
            </Link>
          }
        />
      </div>
    );
  }

  const { stats } = cellar;

  const ocupacao = stats.capacity > 0 ? stats.occupied / stats.capacity : 0;

  return (
    <div className="space-y-4">
      <PageHeader
        titulo={cellar.name}
        subtitulo={`${stats.occupied} de ${stats.capacity} posições ocupadas`}
        voltar="/"
        acao={
          <button
            type="button"
            className="btn-secondary px-3"
            onClick={() => setEditando((v) => !v)}
          >
            {editando ? 'Concluir' : 'Editar'}
          </button>
        }
      />

      {/* Resumo compacto: no celular, três cartões grandes de números empurravam
          a primeira prateleira para fora da tela. */}
      <section className="space-y-2">
        <div
          className="h-2 overflow-hidden rounded-full bg-adega-surface-2"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={stats.capacity}
          aria-valuenow={stats.occupied}
          aria-label="Ocupação da adega"
        >
          <div
            className="h-full rounded-full bg-adega-wine-light"
            style={{ width: `${Math.round(ocupacao * 100)}%` }}
          />
        </div>
        {/* "nas prateleiras" e "sem posição" separados: somar tudo como
            "guardadas" não batia com a contagem por tipo, que inclui as sem posição. */}
        <p className="text-sm text-adega-muted">
          <strong className="text-adega-text">{stats.occupied}</strong> nas prateleiras
          {stats.unplaced > 0 ? (
            <>
              {' · '}
              <strong className="text-adega-gold">{stats.unplaced}</strong> sem posição
            </>
          ) : null}
          {' · '}
          <strong className="text-adega-text">{stats.free}</strong> livres ·{' '}
          <strong className="text-adega-text">{stats.consumed}</strong> já bebidas
        </p>

        {cellar.climatizacao || cellar.papel ? (
          <p className="text-xs text-adega-muted">
            {[
              cellar.climatizacao ? CLIMATIZACAO_LABELS[cellar.climatizacao] : null,
              cellar.papel ? PAPEL_LABELS[cellar.papel] : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        ) : null}

        {stats.byType.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {stats.byType.map((item) => (
              <span
                key={item.type ?? 'sem-tipo'}
                className="rounded-full bg-adega-surface-2 px-2.5 py-1 text-xs text-adega-muted"
              >
                {item.type ? WINE_TYPE_LABELS[item.type] : 'Sem tipo'}: {item.count}
              </span>
            ))}
          </div>
        ) : null}
      </section>

      {editando ? (
        <section className="card space-y-2">
          <h2 className="font-semibold">Climatização</h2>
          <p className="text-xs text-adega-muted">
            Entra na análise da IA: adega sem climatização encurta a janela de guarda de verdade.
            Garrafas já analisadas só mudam quando forem reanalisadas.
          </p>
          <EscolhaClimatizacao
            valor={cellar.climatizacao}
            disabled={atualizar.isPending}
            onChange={(climatizacao) => void atualizar.mutateAsync({ id: cellar.id, climatizacao })}
          />
          {atualizar.error ? <ErrorBox erro={atualizar.error} /> : null}
        </section>
      ) : null}

      {editando ? (
        <section className="card space-y-2">
          <h2 className="font-semibold">Para que serve</h2>
          <p className="text-xs text-adega-muted">
            Numa adega de passagem a garrafa fica pouco tempo, então a condição do móvel não
            encurta a janela — a análise estima pelo vinho, e não pelo lugar.
          </p>
          <EscolhaPapel
            valor={cellar.papel}
            disabled={atualizar.isPending}
            onChange={(papel) => void atualizar.mutateAsync({ id: cellar.id, papel })}
          />
        </section>
      ) : null}

      {editando ? <ShelfEditor cellar={cellar} /> : null}

      {stats.occupied === 0 && stats.unplaced === 0 ? (
        <EmptyState
          titulo="Esta adega está vazia"
          descricao="Cadastre a primeira garrafa tirando uma foto do rótulo."
          acao={
            <Link to="/adicionar" className="btn-primary">
              Adicionar garrafa
            </Link>
          }
        />
      ) : null}

      {/* Tocar numa posição livre abre o cadastro já apontando para ela: é o gesto
          natural de quem está olhando a vaga vazia na adega física. */}
      <CellarGrid
        cellar={cellar}
        onAbrirGarrafa={(id) => navigate(`/garrafa/${id}`)}
        onSelecionarVaga={(shelf, position) =>
          navigate(`/adicionar?adega=${cellar.id}&prateleira=${shelf.id}&posicao=${position}`)
        }
      />

      {semPosicao && semPosicao.length > 0 ? (
        <section className="space-y-2">
          <h2 className="font-semibold">Sem posição ({semPosicao.length})</h2>
          <p className="text-xs text-adega-muted">
            Estas garrafas são desta adega, mas ainda não têm lugar definido.
          </p>
          {semPosicao.map((bottle) => (
            <BottleCard key={bottle.id} bottle={bottle} />
          ))}
        </section>
      ) : null}
    </div>
  );
}

